// Interface of the pages without a session (PR C24 of the redesign, ficha
// 3.51): sign-in and its 2FA step, the SSO callback, the public policies,
// the share viewer and the task-mining connect pages.
//
// `ui_v2` is a tenant flag, and these pages have no tenant: nobody has
// signed in yet (or the visitor has no account at all). So they stay on the
// interface dev always had, and only switch to v2 when this browser forces
// it with `?ui=v2` (the preview override of shell/uiFlags, kept in
// localStorage; `?ui=tenant` or `?ui=v1` clears it). A tenant's flag never
// reaches them, not even the one still in memory after signing out.
//
// Pages shown after sign-in but before the shell (forced password change,
// forced MFA enrollment) do know the tenant and use useUiV2() instead.
import { useLayoutEffect } from 'react';
import type React from 'react';
import { useUiFlagsStore } from '../shell/uiFlags';
import { useUiStore } from '../store/uiStore';

/** True when this browser forces the new interface (`?ui=v2`). */
export function usePublicUiV2(): boolean {
  return useUiFlagsStore((s) => s.override === 'v2');
}

/**
 * usePublicUiV2(), plus the attributes the v2 tokens hang on. Pages outside
 * the tenant provider have no ThemeSync, so with v2 on this sets
 * `data-ui="v2"` and the theme this browser last used on <html>. Without v2
 * it touches nothing, so the page is exactly what it was.
 */
export function usePublicInterface(): boolean {
  const v2 = usePublicUiV2();
  const theme = useUiStore((s) => s.theme);
  useLayoutEffect(() => {
    if (!v2) return;
    document.documentElement.setAttribute('data-ui', 'v2');
    document.documentElement.setAttribute('data-theme', theme);
  }, [v2, theme]);
  return v2;
}

/** Inline style without v2, class with it: one element, the same props and
 *  handlers in both interfaces, so the flow and the inputs never diverge. */
export function v1OrClass(v2: boolean, v1: React.CSSProperties, className: string): { style?: React.CSSProperties; className?: string } {
  return v2 ? { className } : { style: v1 };
}
